import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { GroupDescriptor, groupBy, GroupResult } from '@progress/kendo-data-query';
import {
  AssignmentService,
  AssignmentItem,
  ResourceItem,
  ResourceAssignmentItem
} from '../../../shared/services/assignment.service';

interface ProcessOption { id: number; name: string; }
interface ProductLineOption { id: number; name: string; }
interface UserOption { id: number; displayName: string; email: string; }

@Component({
  selector: 'app-resource-assignments',
  templateUrl: './resource-assignments.component.html',
  styleUrls: ['./resource-assignments.component.css'],
  standalone: false
})
export class ResourceAssignmentsComponent implements OnInit {
  // ---- Tab state ----
  activeTab: 'assignments' | 'resources' = 'assignments';

  // ---- Assignment state ----
  assignments: AssignmentItem[] = [];
  assignmentGroups: GroupDescriptor[] = [{ field: 'process.name' }];
  groupedAssignments: GroupResult[] | AssignmentItem[] = [];
  assignmentSearchTerm = '';
  selectedAssignment: AssignmentItem | null = null;
  isEditingAssignment = false;
  isNewAssignment = false;
  assignmentLoading = false;
  deleteAssignmentDialogOpen = false;

  // ---- Resource state ----
  resources: ResourceItem[] = [];
  filteredResources: ResourceItem[] = [];
  resourceSearchTerm = '';
  selectedResource: ResourceItem | null = null;
  isEditingResource = false;
  isNewResource = false;
  resourceLoading = false;
  deleteResourceDialogOpen = false;

  // ---- Resource Assignment (link) state ----
  assignmentResources: ResourceAssignmentItem[] = [];
  showAddResourceDialog = false;
  addResourceId: number | null = null;
  addResourceIsMain = false;
  filteredAvailableResources: ResourceItem[] = [];
  deleteResourceAssignmentDialogOpen = false;
  selectedResourceAssignment: ResourceAssignmentItem | null = null;

  // ---- Lookup data ----
  processes: ProcessOption[] = [];
  productLines: ProductLineOption[] = [];
  users: UserOption[] = [];

  // ---- Forms ----
  assignmentForm: FormGroup;
  resourceForm: FormGroup;

  constructor(
    private service: AssignmentService,
    private notificationService: NotificationService,
    private fb: FormBuilder
  ) {
    this.assignmentForm = this.fb.group({
      id: [null],
      processId: [null, Validators.required],
      productlineId: [null, Validators.required],
      typeGroupId: [null]
    });

    this.resourceForm = this.fb.group({
      id: [null],
      userId: [null, Validators.required],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadAssignments();
    this.loadResources();
  }

  // =============================================
  // Assignments
  // =============================================

  loadAssignments(): void {
    this.assignmentLoading = true;
    this.service.getAssignments().subscribe({
      next: (data) => {
        this.assignments = data;
        this.groupedAssignments = groupBy(data, this.assignmentGroups);
        this.assignmentLoading = false;

        // Extract unique processes and product lines for dropdowns
        const processMap = new Map<number, string>();
        const plMap = new Map<number, string>();
        data.forEach(a => {
          if (a.process) processMap.set(a.process.id, a.process.name);
          if (a.productLine) plMap.set(a.productLine.id, a.productLine.name);
        });
        this.processes = Array.from(processMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
        this.productLines = Array.from(plMap, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
      },
      error: () => {
        this.showNotification('Failed to load assignments.', 'error');
        this.assignmentLoading = false;
      }
    });
  }

  onAssignmentSelect(assignment: AssignmentItem): void {
    this.selectedAssignment = assignment;
    this.isEditingAssignment = false;
    this.isNewAssignment = false;
    this.loadAssignmentResources(assignment.id!);
  }

  loadAssignmentResources(assignmentId: number): void {
    this.service.getResourceAssignments(assignmentId).subscribe({
      next: (data) => this.assignmentResources = data,
      error: () => this.showNotification('Failed to load assigned resources.', 'error')
    });
  }

  createNewAssignment(): void {
    this.selectedAssignment = null;
    this.isNewAssignment = true;
    this.isEditingAssignment = true;
    this.assignmentResources = [];
    this.assignmentForm.reset();
  }

  editAssignment(): void {
    if (!this.selectedAssignment) return;
    this.isEditingAssignment = true;
    this.assignmentForm.patchValue({
      id: this.selectedAssignment.id,
      processId: this.selectedAssignment.processId,
      productlineId: this.selectedAssignment.productlineId,
      typeGroupId: this.selectedAssignment.typeGroupId
    });
  }

  cancelEditAssignment(): void {
    this.isEditingAssignment = false;
    if (this.isNewAssignment) {
      this.isNewAssignment = false;
      this.selectedAssignment = null;
    }
  }

  saveAssignment(): void {
    if (this.assignmentForm.invalid) return;
    const value = this.assignmentForm.value;

    if (value.id) {
      this.service.updateAssignment(value.id, value).subscribe({
        next: () => {
          this.loadAssignments();
          this.isEditingAssignment = false;
          this.showNotification('Assignment updated.', 'success');
        },
        error: () => this.showNotification('Failed to update assignment.', 'error')
      });
    } else {
      this.service.createAssignment(value).subscribe({
        next: (created) => {
          this.loadAssignments();
          this.selectedAssignment = created;
          this.isEditingAssignment = false;
          this.isNewAssignment = false;
          this.showNotification('Assignment created.', 'success');
        },
        error: () => this.showNotification('Failed to create assignment.', 'error')
      });
    }
  }

  confirmDeleteAssignment(): void {
    this.deleteAssignmentDialogOpen = true;
  }

  deleteAssignment(): void {
    if (!this.selectedAssignment?.id) return;
    this.service.deleteAssignment(this.selectedAssignment.id).subscribe({
      next: () => {
        this.loadAssignments();
        this.selectedAssignment = null;
        this.assignmentResources = [];
        this.deleteAssignmentDialogOpen = false;
        this.showNotification('Assignment deleted.', 'success');
      },
      error: () => {
        this.deleteAssignmentDialogOpen = false;
        this.showNotification('Failed to delete assignment.', 'error');
      }
    });
  }

  // =============================================
  // Resources
  // =============================================

  loadResources(): void {
    this.resourceLoading = true;
    this.service.getResources().subscribe({
      next: (data) => {
        this.resources = data;
        this.filteredResources = [...data];
        this.resourceLoading = false;

        // Extract users for dropdown
        const userMap = new Map<number, UserOption>();
        data.forEach(r => {
          if (r.user) userMap.set(r.user.id, { id: r.user.id, displayName: r.user.displayName, email: r.user.email });
        });
        this.users = Array.from(userMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
      },
      error: () => {
        this.showNotification('Failed to load resources.', 'error');
        this.resourceLoading = false;
      }
    });
  }

  onResourceSelect(resource: ResourceItem): void {
    this.selectedResource = resource;
    this.isEditingResource = false;
    this.isNewResource = false;
  }

  createNewResource(): void {
    this.selectedResource = null;
    this.isNewResource = true;
    this.isEditingResource = true;
    this.resourceForm.reset({ isActive: true });
  }

  editResource(): void {
    if (!this.selectedResource) return;
    this.isEditingResource = true;
    this.resourceForm.patchValue({
      id: this.selectedResource.id,
      userId: this.selectedResource.userId,
      isActive: this.selectedResource.isActive
    });
  }

  cancelEditResource(): void {
    this.isEditingResource = false;
    if (this.isNewResource) {
      this.isNewResource = false;
      this.selectedResource = null;
    }
  }

  saveResource(): void {
    if (this.resourceForm.invalid) return;
    const value = this.resourceForm.value;

    if (value.id) {
      this.service.updateResource(value.id, value).subscribe({
        next: () => {
          this.loadResources();
          this.isEditingResource = false;
          this.showNotification('Resource updated.', 'success');
        },
        error: () => this.showNotification('Failed to update resource.', 'error')
      });
    } else {
      this.service.createResource(value).subscribe({
        next: (created) => {
          this.loadResources();
          this.selectedResource = created;
          this.isEditingResource = false;
          this.isNewResource = false;
          this.showNotification('Resource created.', 'success');
        },
        error: () => this.showNotification('Failed to create resource.', 'error')
      });
    }
  }

  confirmDeleteResource(): void {
    this.deleteResourceDialogOpen = true;
  }

  deleteResource(): void {
    if (!this.selectedResource?.id) return;
    this.service.deleteResource(this.selectedResource.id).subscribe({
      next: () => {
        this.loadResources();
        this.selectedResource = null;
        this.deleteResourceDialogOpen = false;
        this.showNotification('Resource deleted.', 'success');
      },
      error: () => {
        this.deleteResourceDialogOpen = false;
        this.showNotification('Failed to delete resource.', 'error');
      }
    });
  }

  // =============================================
  // Resource ↔ Assignment links
  // =============================================

  openAddResourceDialog(): void {
    this.addResourceId = null;
    this.addResourceIsMain = false;
    this.getAvailableResources();
    this.showAddResourceDialog = true;
  }

  addResourceToAssignment(): void {
    if (!this.selectedAssignment?.id || !this.addResourceId) return;
    this.service.addResourceToAssignment(this.selectedAssignment.id, this.addResourceId, this.addResourceIsMain).subscribe({
      next: () => {
        this.loadAssignmentResources(this.selectedAssignment!.id!);
        this.showAddResourceDialog = false;
        this.showNotification('Resource added to assignment.', 'success');
      },
      error: () => this.showNotification('Failed to add resource.', 'error')
    });
  }

  toggleMainResource(ra: ResourceAssignmentItem): void {
    this.service.updateResourceAssignment(ra.id!, !ra.isMain).subscribe({
      next: () => this.loadAssignmentResources(this.selectedAssignment!.id!),
      error: () => this.showNotification('Failed to update resource assignment.', 'error')
    });
  }

  confirmRemoveResourceAssignment(ra: ResourceAssignmentItem): void {
    this.selectedResourceAssignment = ra;
    this.deleteResourceAssignmentDialogOpen = true;
  }

  removeResourceAssignment(): void {
    if (!this.selectedResourceAssignment?.id) return;
    this.service.removeResourceFromAssignment(this.selectedResourceAssignment.id).subscribe({
      next: () => {
        this.loadAssignmentResources(this.selectedAssignment!.id!);
        this.deleteResourceAssignmentDialogOpen = false;
        this.selectedResourceAssignment = null;
        this.showNotification('Resource removed from assignment.', 'success');
      },
      error: () => {
        this.deleteResourceAssignmentDialogOpen = false;
        this.showNotification('Failed to remove resource.', 'error');
      }
    });
  }

  // =============================================
  // Helpers
  // =============================================

  onAssignmentSearch(term: string): void {
    this.assignmentSearchTerm = term;
    this.filterAssignments();
  }

  private filterAssignments(): void {
    const term = (this.assignmentSearchTerm || '').toLowerCase();
    if (!term) {
      this.groupedAssignments = groupBy(this.assignments, this.assignmentGroups);
      return;
    }
    const filtered = this.assignments.filter(a =>
      (a.process?.name || '').toLowerCase().includes(term) ||
      (a.productLine?.name || '').toLowerCase().includes(term) ||
      (a.typeGroup as any)?.description?.toLowerCase().includes(term) ||
      (a.resourceAssignments || []).some(ra =>
        (ra.resource?.user?.displayName || '').toLowerCase().includes(term)
      )
    );
    this.groupedAssignments = groupBy(filtered, this.assignmentGroups);
  }

  onResourceSearch(term: string): void {
    this.resourceSearchTerm = term;
    this.filterResourceList();
  }

  private filterResourceList(): void {
    const term = (this.resourceSearchTerm || '').toLowerCase();
    if (!term) {
      this.filteredResources = [...this.resources];
      return;
    }
    this.filteredResources = this.resources.filter(r =>
      (r.user?.displayName || '').toLowerCase().includes(term) ||
      (r.user?.email || '').toLowerCase().includes(term)
    );
  }

  getAvailableResources(): ResourceItem[] {
    const assignedIds = new Set(this.assignmentResources.map(ra => ra.resourceId));
    const available = this.resources.filter(r => r.isActive && !assignedIds.has(r.id!));
    this.filteredAvailableResources = available;
    return available;
  }

  onAddResourceFilter(filter: string): void {
    const assignedIds = new Set(this.assignmentResources.map(ra => ra.resourceId));
    const available = this.resources.filter(r => r.isActive && !assignedIds.has(r.id!));
    const term = (filter || '').toLowerCase();
    this.filteredAvailableResources = available.filter(r =>
      r.user?.displayName?.toLowerCase().includes(term) ||
      r.user?.email?.toLowerCase().includes(term)
    );
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 300 },
      hideAfter: 3000
    });
  }
}
